/**
 * Animated marquee text following an SVG wave path.
 * Handles desktop (2-cycle) and mobile (1-cycle) SVGs automatically.
 */
class ClickairWaveMarquee extends HTMLElement {
  #raf = 0;
  #lastTime = 0;
  #offset = 0;
  #loopLength = 0;
  #speed = 40;
  #direction = -1;
  #currentMode = null;
  #activeTextPath = null;

  connectedCallback() {
    // Wait one frame so SVGs are laid out and have real dimensions
    requestAnimationFrame(() => this.#init());
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#raf);
    window.removeEventListener('resize', this.#onResize);
  }

  // Debounced resize handler — only reinits when breakpoint crosses 749px
  #resizeTimer = 0;
  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => {
      const newMode = window.innerWidth <= 749 ? 'mobile' : 'desktop';
      if (newMode !== this.#currentMode) {
        cancelAnimationFrame(this.#raf);
        this.#raf = 0;
        this.#lastTime = 0;
        this.#offset = 0;
        this.#currentMode = null;
        this.#activeTextPath = null;
        requestAnimationFrame(() => this.#init());
      }
    }, 200);
  };

  #init() {
    const mode = window.innerWidth <= 749 ? 'mobile' : 'desktop';
    if (mode === this.#currentMode) return;
    this.#currentMode = mode;

    const svg = this.querySelector(`[data-wave-svg="${mode}"]`);
    if (!svg) return;

    const textPath = svg.querySelector('[data-text-path]');
    const measureEl = svg.querySelector('[data-measure-text]');
    if (!textPath || !measureEl) return;

    this.#setup(svg, textPath, measureEl);
  }

  #setup(svg, textPath, measureEl) {
    const phrase = (this.dataset.text || 'BREATHE BETTER')
      .replace(/\s*[·•]\s*/g, '')
      .trim()
      .toUpperCase();

    this.#speed = (Number(this.dataset.speed) || 25) * 1.6;
    this.#direction = this.dataset.direction === 'normal' ? 1 : -1;

    // Convert gap from screen-px to SVG user units
    const vbWidth = parseFloat(svg.getAttribute('viewBox')?.split(' ')[2] || '1440');
    const renderedWidth = svg.getBoundingClientRect().width || vbWidth;
    const pxToUnits = vbWidth / renderedWidth;
    const gapUnits = Math.round(22 * pxToUnits);

    // Measure phrase and dot in user units using the hidden text element
    measureEl.textContent = phrase;
    const phraseLen = measureEl.getComputedTextLength();
    measureEl.textContent = '·';
    const dotLen = measureEl.getComputedTextLength();
    measureEl.textContent = '';

    if (phraseLen <= 0) {
      // Font not ready — retry shortly
      setTimeout(() => {
        if (this.#currentMode) this.#setup(svg, textPath, measureEl);
      }, 150);
      return;
    }

    // One logical unit = phrase + gap + dot + gap
    this.#loopLength = phraseLen + gapUnits + dotLen + gapUnits;

    // Fill textPath with enough repetitions to cover viewport + offscreen buffer
    const repeats = Math.max(14, Math.ceil(5400 / this.#loopLength) + 4);
    let html = `<tspan>${phrase}</tspan>`;
    for (let i = 0; i < repeats; i++) {
      html += `<tspan dx="${gapUnits}">·</tspan><tspan dx="${gapUnits}">${phrase}</tspan>`;
    }
    textPath.innerHTML = html;

    this.#activeTextPath = textPath;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }

  #tick(time) {
    if (!this.isConnected || !this.#activeTextPath) return;

    if (!this.#lastTime) this.#lastTime = time;
    const delta = Math.min((time - this.#lastTime) / 1000, 0.05);
    this.#lastTime = time;

    this.#offset -= this.#direction * this.#speed * delta;

    if (this.#offset <= -this.#loopLength) this.#offset += this.#loopLength;
    else if (this.#offset >= this.#loopLength) this.#offset -= this.#loopLength;

    this.#activeTextPath.setAttribute('startOffset', `${this.#offset}`);
    this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }
}

if (!customElements.get('clickair-wave-marquee')) {
  customElements.define('clickair-wave-marquee', ClickairWaveMarquee);
}
